import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
} from '@angular/router';

// Key used in route data to explicitly mark routes for reuse.
// Example: { path: 'some-path', component: SomeComponent, data: { reuseRoute: true } }
export const REUSE_ROUTE_DATA_KEY = 'reuseRoute';

@Injectable({ providedIn: 'root' })
export class CustomRouteReuseStrategy implements RouteReuseStrategy {
  // Stores detached route handles
  private storedHandles = new Map<string, DetachedRouteHandle | null>();

  // Determines if this route (and its subtree) should be detached to be reused later.
  // Called when navigating away from a route.
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    const shouldReuse = this.isRouteConfiguredForReuse(route);
    return shouldReuse;
  }

  // Stores the detached route handle.
  // Called only if shouldDetach returned true.
  store(
    route: ActivatedRouteSnapshot,
    handle: DetachedRouteHandle | null
  ): void {
    if (this.isRouteConfiguredForReuse(route)) {
      const key = this.getRouteKey(route);
      this.storedHandles.set(key, handle);
    }
  }

  // Determines if this route (and its subtree) should be reattached.
  // Called when navigating *to* a route. Checks if a handle was previously stored.
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const key = this.getRouteKey(route);
    const canAttach =
      this.storedHandles.has(key) && this.storedHandles.get(key) !== null;
    const shouldReuse = this.isRouteConfiguredForReuse(route);
    return canAttach && shouldReuse;
  }

  // Retrieves the previously stored route handle.
  // Called only if shouldAttach returned true.
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.getRouteKey(route);
    const handle = this.storedHandles.get(key) ?? null;
    // Optional: Clear the handle once retrieved if you want single-use reuse,
    // but typically you want to keep it for multiple navigations back.
    // this.storedHandles.delete(key);
    return handle;
  }

  // Determines if a route should be reused.
  // Compares future and current route snapshots.
  // Default behavior compares route configuration - usually sufficient.
  shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot
  ): boolean {
    const reuse = future.routeConfig === curr.routeConfig;
    return reuse;
  }

  // Checks if a route is configured for reuse based on its path or data flag.
  private isRouteConfiguredForReuse(route: ActivatedRouteSnapshot): boolean {
    // Check explicit data flag first
    if (route.data?.[REUSE_ROUTE_DATA_KEY] === true) {
      return true;
    }
    // Check configured paths (primarily for top-level routes)
    const path = route.routeConfig?.path;
    return path !== null && path !== undefined;
  }

  // Generates a unique key for storing/retrieving route handles.
  // Using the route's configured path is usually sufficient for distinct routes.
  // For complex scenarios (e.g., child routes with same path segments), a more robust key might be needed.
  private getRouteKey(route: ActivatedRouteSnapshot): string {
    // Combine path segments and outlet name for uniqueness
    let key = route.pathFromRoot
      .map((s) => s.routeConfig?.path ?? '')
      .join('/'); // e.g., "/info/" for the outlet route
    if (route.outlet && route.outlet !== 'primary') {
      key += `(outlet:${route.outlet})`; // e.g., "/info/(outlet:controls)"
    }
    return key;
  }
}
